/**
 * @typedef {Object} LatLong
 *
 * @property {number} latitude
 * @property {number} longitude
 */

const { getGeolocation } = window.BAO.Utils

const earthRadius = 6371e3

/**
 * @param {LatLong} from
 * @param {LatLong} to
 */
function getPythagoreanDistanceBetweenTwoPoints (from, to) {
  const R = earthRadius
  const x =
    (to.longitude - from.longitude) *
    Math.cos((from.latitude + to.latitude) / 2)
  const y = to.latitude - from.latitude

  return Math.sqrt(x ** 2 + y ** 2) * R
}

class StockistList extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['list', 'stockist']
  }

  async connectedCallback () {
    super.connectedCallback()

    /** `window.navigator.permissions` is not supported in Safari 15 so we preemptively sort the stockists in this instance. */
    if (!window.navigator.permissions) {
      this.listeners.add(
        document.body,
        'site-drawer:opened',
        this.handleDrawerOpen.bind(this),
      )
    } else {
      const permissions = await window.navigator.permissions.query({
        name: 'geolocation',
      })
      if (permissions.state === 'granted') {
        this.sortStockistsByProximity()
      } else {
        this.listeners.add(
          document.body,
          'site-drawer:opened',
          this.handleDrawerOpen.bind(this),
        )
      }
    }
  }

  /** @param {CustomEvent} event */
  handleDrawerOpen (event) {
    if (event.target.key !== 'stock') {
      return
    }

    this.sortStockistsByProximity()
  }

  getLocation () {
    return getGeolocation()
  }

  async sortStockistsByProximity () {
    /** @type {GeolocationPosition} */
    let currentLocation
    try {
      currentLocation = await getGeolocation()
    } catch (error) {
      /* If error, user declined to share geolocation. */
      return
    }

    const sortedStockists = this.getSortedStockists(currentLocation)
    const clones = sortedStockists.map(stockist =>
      stockist.element.cloneNode(true),
    )

    this.els.list.element.replaceChildren(...clones)
  }

  /**
   * @param {LatLong} userLocation
   *
   * @warn Distance is calculated as the crow flies and isn't super accurate because we're assuming the earth is a perfect sphere here.
   * If we ever need to display distances we should consider switching to something like Google's Distance Matrix API which can give us
   * distances in terms of car journey length.
   */
  getSortedStockists (userLocation) {
    const sortableStockists = this.stockists.filter(stockist => stockist.coords)
    const unsortableStockists = this.stockists.filter(
      stockist => !stockist.coords,
    )

    const sortedStockists = [...sortableStockists].sort((a, b) => {
      const distanceA = getPythagoreanDistanceBetweenTwoPoints(
        a.coords,
        userLocation.coords,
      )
      const distanceB = getPythagoreanDistanceBetweenTwoPoints(
        b.coords,
        userLocation.coords,
      )

      return distanceA - distanceB
    })

    return [...sortedStockists, ...unsortableStockists]
  }

  /** @returns {{ coords?: LatLong; element: HTMLLIElement; }[]} */
  get stockists () {
    return this.els.stockist.elements.map(element => {
      const base = { element }

      if (
        'storeLatitude' in element.dataset &&
        'storeLongitude' in element.dataset
      ) {
        base.coords = {
          latitude: Number(element.dataset.storeLatitude),
          longitude: Number(element.dataset.storeLongitude),
        }
      }

      return base
    })
  }
}

if (!window.customElements.get('stockist-list')) {
  window.customElements.define('stockist-list', StockistList)
}
